import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconsModule } from '../../../icons/icons.module';

@Component({
  selector: 'zikatagov-empty-state',
  standalone: true,
  imports: [CommonModule, IconsModule],
  template: `
    <div class="empty-state-wrap">

      <!-- Decorative blobs -->
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>

      <!-- Icon ring -->
      <div class="icon-ring">
        <div class="icon-ring-inner">
          <ng-icon [name]="icon" size="36" class="icon-glyph"></ng-icon>
        </div>
      </div>

      <!-- Text block -->
      <h3 class="es-title">{{ title }}</h3>

      <p class="es-intro" *ngIf="introText">{{ introText }}</p>
      <p class="es-desc"  *ngIf="description">{{ description }}</p>

      <!-- Instructions -->
      <div class="es-instructions" *ngIf="instructions.length">
        <p class="es-inst-title" *ngIf="instructionTitle">{{ instructionTitle }}</p>
        <ul class="es-inst-list">
          <li *ngFor="let instruction of instructions" class="es-inst-item">
            <span class="inst-check">
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            {{ instruction }}
          </li>
        </ul>
      </div>

      <!-- Watch Video -->
      <button *ngIf="showVideo" class="video-btn">
        <span class="video-play">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
        </span>
        Watch Video
      </button>

      <!-- Divider -->
      <div class="es-divider" *ngIf="hasAction"></div>

      <p class="es-cta-text" *ngIf="hasAction && ctaText">{{ ctaText }}</p>

      <!-- Action button -->
      <button *ngIf="hasAction" class="es-action-btn" (click)="onActionClick()">
        <ng-icon name="saxAddOutline" size="16"></ng-icon>
        {{ actionButtonText }}
      </button>

    </div>
  `,
  styles: [`
    :host {
      display: block;
      font-family: 'DM Sans', 'Segoe UI', sans-serif;
    }

    /* ── Wrap ── */
    .empty-state-wrap {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 3.5rem 2rem;
      margin: 1.5rem;
      background: #ffffff;
      border-radius: 20px;
      border: 1.5px solid #f0eafa;
      box-shadow: 0 4px 32px rgba(109, 40, 217, 0.06);
      overflow: hidden;
    }

    /* ── Decorative blobs ── */
    .blob {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
    }

    .blob-1 {
      width: 320px;
      height: 320px;
      top: -120px;
      right: -80px;
      background: radial-gradient(circle, rgba(167,139,250,0.12) 0%, transparent 70%);
    }

    .blob-2 {
      width: 240px;
      height: 240px;
      bottom: -80px;
      left: -60px;
      background: radial-gradient(circle, rgba(249,168,212,0.14) 0%, transparent 70%);
    }

    /* ── All content above blobs ── */
    .icon-ring, .es-title, .es-intro, .es-desc,
    .es-instructions, .video-btn, .es-divider,
    .es-cta-text, .es-action-btn {
      position: relative;
      z-index: 1;
    }

    /* ── Icon ring ── */
    .icon-ring {
      width: 88px;
      height: 88px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f3eeff 0%, #fce7f3 100%);
      border: 2px solid #ede9fb;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
      box-shadow: 0 4px 20px rgba(124, 58, 237, 0.12);
    }

    .icon-ring-inner {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: linear-gradient(135deg, #7c3aed 0%, #f97316 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 14px rgba(124, 58, 237, 0.3);
    }

    .icon-glyph {
      color: #ffffff;
    }

    /* ── Text ── */
    .es-title {
      font-size: 1.25rem;
      font-weight: 700;
      letter-spacing: -0.025em;
      color: #1a003d;
      margin: 0 0 0.75rem;
    }

    .es-intro {
      font-size: 0.9rem;
      font-weight: 500;
      color: #4b5563;
      margin: 0 0 0.375rem;
      max-width: 420px;
    }

    .es-desc {
      font-size: 0.85rem;
      color: #6b7280;
      font-weight: 400;
      margin: 0 0 1.5rem;
      max-width: 440px;
      line-height: 1.6;
    }

    /* ── Instructions ── */
    .es-instructions {
      background: #faf8ff;
      border: 1.5px solid #ede9fb;
      border-radius: 12px;
      padding: 1rem 1.25rem;
      margin-bottom: 1.5rem;
      max-width: 420px;
      width: 100%;
      text-align: left;
    }

    .es-inst-title {
      font-size: 0.75rem;
      font-weight: 700;
      color: #7c3aed;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 0 0 0.625rem;
    }

    .es-inst-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .es-inst-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      font-size: 0.82rem;
      color: #4b5563;
      font-weight: 400;
      line-height: 1.5;
    }

    .inst-check {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #7c3aed, #f97316);
      color: white;
      flex-shrink: 0;
      margin-top: 1px;
    }

    /* ── Video button ── */
    .video-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.625rem 1.5rem;
      border-radius: 100px;
      border: 1.5px solid #ede9fb;
      background: #faf8ff;
      color: #7c3aed;
      font-size: 0.82rem;
      font-weight: 600;
      cursor: pointer;
      margin-bottom: 1.5rem;
      transition: all 0.15s ease;
    }

    .video-btn:hover {
      background: #f3eeff;
      border-color: #c4b5fd;
      box-shadow: 0 2px 8px rgba(124, 58, 237, 0.12);
    }

    .video-play {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: linear-gradient(135deg, #7c3aed, #f97316);
      color: white;
      flex-shrink: 0;
    }

    /* ── Divider ── */
    .es-divider {
      width: 60%;
      height: 1px;
      background: linear-gradient(90deg, transparent, #f0eafa 30%, #f9d5ea 70%, transparent);
      margin: 0 0 1.25rem;
    }

    /* ── CTA text ── */
    .es-cta-text {
      font-size: 0.8rem;
      color: #9ca3af;
      font-weight: 400;
      margin: 0 0 1rem;
    }

    /* ── Action button ── */
    .es-action-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 2rem;
      border-radius: 12px;
      border: none;
      background: linear-gradient(135deg, #7c3aed 0%, #f97316 100%);
      color: #ffffff;
      font-size: 0.9rem;
      font-weight: 700;
      cursor: pointer;
      letter-spacing: -0.01em;
      box-shadow: 0 4px 18px rgba(124, 58, 237, 0.3);
      transition: all 0.2s ease;
    }

    .es-action-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(124, 58, 237, 0.4);
    }

    .es-action-btn:active {
      transform: translateY(0);
    }

    /* ── Responsive ── */
    @media (max-width: 480px) {
      .empty-state-wrap {
        padding: 2.5rem 1.25rem;
        margin: 0.75rem;
      }

      .es-title { font-size: 1.1rem; }
      .icon-ring { width: 72px; height: 72px; }
      .icon-ring-inner { width: 52px; height: 52px; }
    }
  `],
})
export class ZikataEmptyStateComponent {
  @Input() icon = 'saxNotificationOutline';
  @Input() title = 'Welcome to Zikata';
  @Input() introText = '';
  @Input() description = '';
  @Input() instructionTitle = '';
  @Input() instructions: string[] = [];
  @Input() showVideo = false;
  @Input() ctaText = 'Tap on the button below to get started';
  @Input() actionButtonText = 'Get Started';
  @Input() hasAction = true;
  @Output() actionClicked = new EventEmitter<void>();
  @Output() actionClick = new EventEmitter<void>();

  onActionClick() {
    this.actionClicked.emit();
    this.actionClick.emit();
  }
}